import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiSend,
  FiFileText,
  FiPaperclip,
  FiTrash2,
  FiUsers,
  FiShield,
  FiBriefcase,
  FiArrowLeft,
  FiUploadCloud,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import communicationService from "../../services/communicationService";
import collegeService from "../../services/collegeService";
import courseService from "../../services/courseService";

export default function CommunicationForm({ onSuccess, onCancel, isModal = false }) {
  const { user, role: contextRole } = useApp();
  const navigate = useNavigate();

  const userRole = (user?.role || contextRole || "").toLowerCase();
  const isSuperAdmin = userRole === "superadmin";
  const isAdmin = userRole === "admin";
  const isTeacher = userRole === "teacher";

  // Form State
  const [title, setTitle] = useState("");
  const [type, setType] = useState("circular");
  const [priority, setPriority] = useState("normal");
  const [message, setMessage] = useState("");

  // Audience State
  // Super Admin: array of ["admins", "employees", "students"]
  // Admin: "students", "employees", or "both"
  // Teacher: "students"
  const [superRoles, setSuperRoles] = useState(["students", "employees"]);
  const [adminTarget, setAdminTarget] = useState("both"); // "students", "employees", "both"

  // College Targeting (for Super Admin)
  const [collegesList, setCollegesList] = useState([]);
  const [allColleges, setAllColleges] = useState(true);
  const [selectedColleges, setSelectedColleges] = useState([]);

  // Course & Year Targeting (for Students audience)
  const [coursesList, setCoursesList] = useState([]);
  const [allCourses, setAllCourses] = useState(true);
  const [selectedCourses, setSelectedCourses] = useState([]);

  const [allYears, setAllYears] = useState(true);
  const [selectedYears, setSelectedYears] = useState([]);

  // Attachments
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  // Load Colleges (for Super Admin) and Courses
  useEffect(() => {
    if (isSuperAdmin) {
      collegeService
        .getAll()
        .then((res) => {
          const list = res.data?.data || res.data || [];
          setCollegesList(Array.isArray(list) ? list : []);
        })
        .catch(() => {});
    }

    courseService
      .getAll()
      .then((res) => {
        const list = res.data?.data || res.data || [];
        setCoursesList(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, [isSuperAdmin]);

  // Handle Attachment Upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      toast.error("File size exceeds 15MB limit");
      return;
    }

    setUploadingFile(true);
    try {
      const res = await communicationService.uploadAttachment(file);
      // Accommodate unwrapped response { success: true, data: {...} } or direct {...}
      const attachedItem = res?.data?.url
        ? res.data
        : res?.data?.data?.url
        ? res.data.data
        : res?.url
        ? res
        : null;

      if (attachedItem && attachedItem.url) {
        setAttachments((prev) => [...prev, attachedItem]);
        toast.success(`Attached "${file.name}"`);
      } else {
        toast.error(res?.message || "Failed to attach document");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to attach document");
    } finally {
      setUploadingFile(false);
      e.target.value = "";
    }
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Determine if student filtering options should be shown
  const isStudentsTargeted = () => {
    if (isTeacher) return true;
    if (isAdmin) return adminTarget === "students" || adminTarget === "both";
    if (isSuperAdmin) return superRoles.includes("students");
    return false;
  };

  const toggleSuperRole = (roleKey) => {
    if (superRoles.includes(roleKey)) {
      if (superRoles.length === 1) {
        toast.error("At least one target role is required.");
        return;
      }
      setSuperRoles(superRoles.filter((r) => r !== roleKey));
    } else {
      setSuperRoles([...superRoles, roleKey]);
    }
  };

  const toggleCollege = (collegeId) => {
    if (selectedColleges.includes(collegeId)) {
      setSelectedColleges(selectedColleges.filter((id) => id !== collegeId));
    } else {
      setSelectedColleges([...selectedColleges, collegeId]);
    }
  };

  const toggleCourse = (courseId) => {
    if (selectedCourses.includes(courseId)) {
      setSelectedCourses(selectedCourses.filter((id) => id !== courseId));
    } else {
      setSelectedCourses([...selectedCourses, courseId]);
    }
  };

  const toggleYear = (yearNum) => {
    if (selectedYears.includes(yearNum)) {
      setSelectedYears(selectedYears.filter((y) => y !== yearNum));
    } else {
      setSelectedYears([...selectedYears, yearNum]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Please enter a title / subject");
      return;
    }
    if (!message.trim()) {
      toast.error("Please enter message content");
      return;
    }

    // Resolve audience roles
    let finalRoles = [];
    if (isSuperAdmin) {
      finalRoles = superRoles;
    } else if (isAdmin) {
      if (adminTarget === "both") finalRoles = ["students", "employees"];
      else if (adminTarget === "students") finalRoles = ["students"];
      else finalRoles = ["employees"];
    } else if (isTeacher) {
      finalRoles = ["students"];
    }

    if (finalRoles.length === 0) {
      toast.error("Please select at least one target audience");
      return;
    }

    // Validate college selection if superadmin
    if (isSuperAdmin && !allColleges && selectedColleges.length === 0) {
      toast.error("Please select at least one college or choose 'All Colleges'");
      return;
    }

    // Validate course & year if specific chosen
    if (isStudentsTargeted()) {
      if (!allCourses && selectedCourses.length === 0) {
        toast.error("Please select at least one course or choose 'All Courses'");
        return;
      }
      if (!allYears && selectedYears.length === 0) {
        toast.error("Please select at least one year or choose 'All Years'");
        return;
      }
    }

    const payload = {
      title: title.trim(),
      message: message.trim(),
      type,
      priority,
      targetAudience: {
        roles: finalRoles,
        allColleges: isSuperAdmin ? allColleges : false,
        colleges: isSuperAdmin ? (allColleges ? [] : selectedColleges) : undefined,
        allCourses: isStudentsTargeted() ? allCourses : true,
        courses: isStudentsTargeted() && !allCourses ? selectedCourses : [],
        allYears: isStudentsTargeted() ? allYears : true,
        years: isStudentsTargeted() && !allYears ? selectedYears : [],
      },
      attachments,
    };

    setSubmitting(true);
    try {
      const res = await communicationService.create(payload);
      // api.js response interceptor unwraps response.data directly: { success: true, message: "...", data: {...} }
      const isSuccess = res?.success || res?.data?._id || res?.data?.success || res?._id;
      if (isSuccess) {
        toast.success(res?.message || "Communication dispatched successfully!");
        if (onSuccess) {
          onSuccess(res?.data || res);
        } else {
          if (isTeacher) navigate("/teacher/communication");
          else navigate("/communications");
        }
      } else {
        toast.error(res?.message || "Failed to dispatch communication");
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to dispatch communication");
    } finally {
      setSubmitting(false);
    }
  };

  const containerStyle = isModal
    ? { padding: "0.5rem 0" }
    : { maxWidth: 900, margin: "0 auto", padding: "1.5rem 1rem" };

  return (
    <div style={containerStyle}>
      {!isModal && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => (onCancel ? onCancel() : navigate(-1))}
            style={{ display: "flex", alignItems: "center", gap: 5 }}
          >
            <FiArrowLeft size={14} /> Back
          </button>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Compose Communication</h1>
            <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "2px 0 0" }}>
              Publish circulars, timetable changes, notices, or messages to targeted groups
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          {/* ── Type & Priority Row ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 600, fontSize: 13 }}>
                Communication Type
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="form-control"
                style={{ fontWeight: 500 }}
              >
                <option value="circular">📜 Circular</option>
                <option value="timetable_change">⏰ Timetable Change</option>
                <option value="notice">📢 Notice / Announcement</option>
                <option value="message">💬 General Message</option>
                <option value="urgent_alert">🚨 Urgent Alert</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 600, fontSize: 13 }}>
                Priority Level
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="form-control"
                style={{
                  fontWeight: 600,
                  color: priority === "urgent" ? "#dc2626" : priority === "high" ? "#d97706" : "#003b67",
                }}
              >
                <option value="normal">Normal</option>
                <option value="high">High Priority</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* ── Title ── */}
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: 600, fontSize: 13 }}>
              Subject / Title <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. Revised Exam Schedule for B.Pharm 2nd Year, College Holiday Notice..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
              style={{ fontSize: 14 }}
            />
          </div>

          {/* ── Target Audience Selector ── */}
          <div
            style={{
              padding: "1.2rem",
              borderRadius: "8px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <FiUsers size={16} color="#003b67" />
              <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Target Audience</span>
            </div>

            {/* SUPER ADMIN AUDIENCE SELECTION */}
            {isSuperAdmin && (
              <div>
                <label style={{ fontSize: 12.5, fontWeight: 600, color: "#475569", marginBottom: 8, display: "block" }}>
                  Select Recipient Roles (Multi-select):
                </label>
                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.45rem",
                      padding: "8px 14px",
                      borderRadius: "6px",
                      border: superRoles.includes("admins") ? "1.5px solid #7c3aed" : "1px solid #cbd5e1",
                      background: superRoles.includes("admins") ? "#f5f3ff" : "#fff",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 13,
                      color: superRoles.includes("admins") ? "#6b21a8" : "#334155",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={superRoles.includes("admins")}
                      onChange={() => toggleSuperRole("admins")}
                    />
                    <FiShield size={14} /> Admins of Colleges
                  </label>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.45rem",
                      padding: "8px 14px",
                      borderRadius: "6px",
                      border: superRoles.includes("employees") ? "1.5px solid #059669" : "1px solid #cbd5e1",
                      background: superRoles.includes("employees") ? "#ecfdf5" : "#fff",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 13,
                      color: superRoles.includes("employees") ? "#047857" : "#334155",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={superRoles.includes("employees")}
                      onChange={() => toggleSuperRole("employees")}
                    />
                    <FiBriefcase size={14} /> Staff / Employees
                  </label>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.45rem",
                      padding: "8px 14px",
                      borderRadius: "6px",
                      border: superRoles.includes("students") ? "1.5px solid #003b67" : "1px solid #cbd5e1",
                      background: superRoles.includes("students") ? "#e8f1f8" : "#fff",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 13,
                      color: superRoles.includes("students") ? "#002747" : "#334155",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={superRoles.includes("students")}
                      onChange={() => toggleSuperRole("students")}
                    />
                    <FiUsers size={14} /> Students
                  </label>
                </div>

                {/* College Selection for Super Admin */}
                <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px dashed #cbd5e1" }}>
                  <label style={{ fontSize: 12.5, fontWeight: 600, color: "#475569", marginBottom: 6, display: "block" }}>
                    College Scope:
                  </label>
                  <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: 8 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: 13, cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="collegeScope"
                        checked={allColleges}
                        onChange={() => setAllColleges(true)}
                      />
                      All Colleges (Group-wide)
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: 13, cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="collegeScope"
                        checked={!allColleges}
                        onChange={() => setAllColleges(false)}
                      />
                      Specific Colleges
                    </label>
                  </div>

                  {!allColleges && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0.5rem", marginTop: 6 }}>
                      {collegesList.map((c) => (
                        <label
                          key={c._id || c.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.4rem",
                            fontSize: 12.5,
                            padding: "5px 8px",
                            background: "#fff",
                            borderRadius: "5px",
                            border: "1px solid #e2e8f0",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedColleges.includes(c._id || c.id)}
                            onChange={() => toggleCollege(c._id || c.id)}
                          />
                          <span style={{ fontWeight: 600 }}>{c.code || c.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ADMIN AUDIENCE SELECTION */}
            {isAdmin && (
              <div>
                <label style={{ fontSize: 12.5, fontWeight: 600, color: "#475569", marginBottom: 8, display: "block" }}>
                  Send Notice / Circular To:
                </label>
                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.45rem",
                      padding: "8px 14px",
                      borderRadius: "6px",
                      border: adminTarget === "both" ? "1.5px solid #003b67" : "1px solid #cbd5e1",
                      background: adminTarget === "both" ? "#e8f1f8" : "#fff",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 13,
                      color: adminTarget === "both" ? "#002747" : "#334155",
                    }}
                  >
                    <input
                      type="radio"
                      name="adminTarget"
                      value="both"
                      checked={adminTarget === "both"}
                      onChange={(e) => setAdminTarget(e.target.value)}
                    />
                    Both Students &amp; Staff / Employees
                  </label>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.45rem",
                      padding: "8px 14px",
                      borderRadius: "6px",
                      border: adminTarget === "students" ? "1.5px solid #003b67" : "1px solid #cbd5e1",
                      background: adminTarget === "students" ? "#e8f1f8" : "#fff",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 13,
                      color: adminTarget === "students" ? "#002747" : "#334155",
                    }}
                  >
                    <input
                      type="radio"
                      name="adminTarget"
                      value="students"
                      checked={adminTarget === "students"}
                      onChange={(e) => setAdminTarget(e.target.value)}
                    />
                    Students Only
                  </label>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.45rem",
                      padding: "8px 14px",
                      borderRadius: "6px",
                      border: adminTarget === "employees" ? "1.5px solid #059669" : "1px solid #cbd5e1",
                      background: adminTarget === "employees" ? "#ecfdf5" : "#fff",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 13,
                      color: adminTarget === "employees" ? "#047857" : "#334155",
                    }}
                  >
                    <input
                      type="radio"
                      name="adminTarget"
                      value="employees"
                      checked={adminTarget === "employees"}
                      onChange={(e) => setAdminTarget(e.target.value)}
                    />
                    Staff / Employees Only
                  </label>
                </div>
              </div>
            )}

            {/* TEACHER AUDIENCE SELECTION */}
            {isTeacher && (
              <div style={{ fontSize: 13, color: "#1e40af", display: "flex", alignItems: "center", gap: 6 }}>
                <FiUsers size={15} /> Sending circular / timetable notice to <strong>Students</strong>
              </div>
            )}

            {/* ── Student Filters (Course & Year) ── */}
            {isStudentsTargeted() && (
              <div
                style={{
                  marginTop: "0.5rem",
                  padding: "0.9rem",
                  background: "#ffffff",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b", marginBottom: 8 }}>
                  🎓 Student Batch &amp; Class Filters:
                </div>

                {/* Course Selection */}
                <div style={{ marginBottom: "0.75rem" }}>
                  <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: "#475569" }}>Courses:</span>
                    <label style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12.5, cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="courseScope"
                        checked={allCourses}
                        onChange={() => setAllCourses(true)}
                      />
                      All Courses
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12.5, cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="courseScope"
                        checked={!allCourses}
                        onChange={() => setAllCourses(false)}
                      />
                      Specific Courses
                    </label>
                  </div>

                  {!allCourses && (
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: 4 }}>
                      {coursesList.map((crs) => (
                        <label
                          key={crs._id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.35rem",
                            fontSize: 12,
                            padding: "4px 8px",
                            background: selectedCourses.includes(crs._id) ? "#e8f1f8" : "#f8fafc",
                            border: selectedCourses.includes(crs._id) ? "1px solid #003b67" : "1px solid #e2e8f0",
                            borderRadius: 4,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCourses.includes(crs._id)}
                            onChange={() => toggleCourse(crs._id)}
                          />
                          <span>{crs.courseCode || crs.courseName}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Year Selection */}
                <div>
                  <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: "#475569" }}>Class Years:</span>
                    <label style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12.5, cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="yearScope"
                        checked={allYears}
                        onChange={() => setAllYears(true)}
                      />
                      All Years (1st, 2nd, 3rd, 4th)
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12.5, cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="yearScope"
                        checked={!allYears}
                        onChange={() => setAllYears(false)}
                      />
                      Specific Years
                    </label>
                  </div>

                  {!allYears && (
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: 4 }}>
                      {[1, 2, 3, 4].map((yr) => (
                        <label
                          key={yr}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.35rem",
                            fontSize: 12,
                            padding: "4px 10px",
                            background: selectedYears.includes(yr) ? "#e8f1f8" : "#f8fafc",
                            border: selectedYears.includes(yr) ? "1px solid #003b67" : "1px solid #e2e8f0",
                            borderRadius: 4,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedYears.includes(yr)}
                            onChange={() => toggleYear(yr)}
                          />
                          <span>{yr}{yr === 1 ? "st" : yr === 2 ? "nd" : yr === 3 ? "rd" : "th"} Year</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Message Content Body ── */}
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: 600, fontSize: 13 }}>
              Message / Circular Content <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <textarea
              className="form-control"
              rows={6}
              placeholder="Write the full circular, timetable instructions, or message details here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              style={{ fontSize: 14, lineHeight: 1.5, fontFamily: "inherit" }}
            />
          </div>

          {/* ── Attachments ── */}
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 5 }}>
              <FiPaperclip size={14} /> Attachments (PDFs, Images, Circular Documents)
            </label>

            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", marginTop: 4 }}>
              <label
                className="btn btn-outline btn-sm"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: uploadingFile ? "not-allowed" : "pointer",
                  opacity: uploadingFile ? 0.6 : 1,
                }}
              >
                <FiUploadCloud size={15} />
                {uploadingFile ? "Uploading..." : "Upload File (Max 15MB)"}
                <input
                  type="file"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                  style={{ display: "none" }}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.xls,.xlsx"
                />
              </label>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                Supported: PDF, Images, Word documents
              </span>
            </div>

            {attachments.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.75rem" }}>
                {attachments.map((att, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "6px 10px",
                      background: "#f1f5f9",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden", textOverflow: "ellipsis" }}>
                      <FiFileText size={14} color="#003b67" />
                      <a href={att.url} target="_blank" rel="noreferrer" style={{ fontWeight: 600, color: "#003b67" }}>
                        {att.name || "Attachment"}
                      </a>
                      {att.size && (
                        <span style={{ fontSize: 11, color: "#64748b" }}>
                          ({(att.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(idx)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc2626",
                        cursor: "pointer",
                        padding: 4,
                      }}
                      title="Remove attachment"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Submit / Action Buttons ── */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.5rem" }}>
            {onCancel && (
              <button
                type="button"
                className="btn btn-outline"
                onClick={onCancel}
                disabled={submitting}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || uploadingFile}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 20px" }}
            >
              <FiSend size={15} />
              {submitting ? "Dispatching..." : "Send Communication"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
